import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: "#FFFFFF",
    fontSize: 10,
    fontFamily: "Helvetica",
  },
  header: {
    marginBottom: 20,
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomWidth: 1,
    borderBottomColor: "#CCCCCC",
    paddingBottom: 10,
  },
  headerLeft: {
    flexDirection: "column",
    width: "65%",
  },
  headerRight: {
    flexDirection: "column",
    width: "35%",
    alignItems: "flex-end",
    textAlign: "right",
  },
  orgName: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#46237A",
    marginBottom: 2,
  },
  orgDetails: {
    fontSize: 8,
    color: "#333333",
    marginBottom: 2,
  },
  receiptTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#333333",
    marginBottom: 2,
  },
  receiptDetails: {
    fontSize: 8,
    color: "#333333",
    marginBottom: 1,
    textAlign: "right",
  },
  section: {
    marginBottom: 10,
    padding: 10,
    borderWidth: 1,
    borderColor: "#EEEEEE",
    borderRadius: 4,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 5,
    color: "#333333",
    textDecoration: "underline",
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 3,
  },
  label: {
    fontSize: 8,
    color: "#666666",
    width: "40%",
  },
  value: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#333333",
    width: "60%",
    textAlign: "right",
  },
  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderColor: "#EEEEEE",
    marginBottom: 10,
  },
  tableRow: {
    margin: "auto",
    flexDirection: "row",
  },
  tableColHeader: {
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "#EEEEEE",
    backgroundColor: "#F9F9F9",
    padding: 4,
  },
  tableCol: {
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "#EEEEEE",
    padding: 4,
  },
  tableCellHeader: {
    margin: "auto",
    fontSize: 8,
    fontWeight: "bold",
    color: "#333333",
  },
  tableCell: {
    margin: "auto",
    fontSize: 8,
    color: "#333333",
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 5,
    paddingTop: 5,
    borderTopWidth: 1,
    borderTopColor: "#000000",
  },
  totalLabel: {
    fontSize: 9,
    fontWeight: "bold",
  },
  totalValue: {
    fontSize: 9,
    fontWeight: "bold",
    textAlign: "right",
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: "center",
    color: "#999999",
    fontSize: 7,
    borderTopWidth: 1,
    borderTopColor: "#CCCCCC",
    paddingTop: 10,
  },
  remark: {
    marginTop: 10,
    padding: 10,
    backgroundColor: "#F9F9F9",
    borderWidth: 1,
    borderColor: "#EEEEEE",
    borderRadius: 4,
  },
  remarkText: {
    fontSize: 8,
    color: "#666666",
    fontStyle: "italic",
  },
  receiptImage: {
    marginTop: 10,
    width: 200,
    height: 200,
    objectFit: "cover",
  },
});

// Generic Receipt Component
const ReceiptPDF = ({ transactionData, type, paymentHistory }) => {
  // Get Organization and Site details from localStorage
  let organization = {};
  let siteData = {};

  try {
    const orgStr = localStorage.getItem("organization");
    if (orgStr) organization = JSON.parse(orgStr);

    const siteStr = localStorage.getItem("siteData");
    if (siteStr) siteData = JSON.parse(siteStr);
  } catch (e) {
    console.error("Error reading from localStorage", e);
  }

  // Helper function to format date
  const formatDate = (dateString) => {
    try {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch (e) {
      return "N/A";
    }
  };

  // Get appropriate title based on transaction type
  const getReceiptTitle = () => {
    switch (type) {
      case "material":
        return "Material Purchase Receipt";
      case "worker":
        return "Worker Payment Receipt";
      case "refund":
        return "Refund Receipt";
      case "received":
        return "Received Payment Receipt";
      case "other":
        return "Transaction Receipt";
      case "vendor":
        return "Vendor Payment Receipt";
      case "contractor":
        return "Contractor Payment Receipt";
      default:
        return "Transaction Receipt";
    }
  };

  // Compute Transaction ID (used as Receipt No) to match UI
  const getDisplayTransactionId = () => {
    const fallbackId = String(transactionData?.id ?? "").padStart(3, "0");
    const shortId = transactionData?.transaction_id || fallbackId;
    switch (type) {
      case "material":
        // Prefer PO code if present; otherwise use Delivery with short id
        return transactionData?.po_number
          ? transactionData.po_number
          : `DLVRY-${shortId}`;
      case "worker":
        return `WORKER_PAY-${shortId}`;
      case "refund":
        return `TRN-${shortId}`;
      case "received":
        return `TRN-${shortId}`;
      case "other":
        return `TRN-${shortId}`;
      default:
        return `TRN-${shortId}`;
    }
  };

  const receiptNumber = getDisplayTransactionId();

  // Calculate Total Tax for Material
  const calculateTotalTax = () => {
    if (type === "material" && Array.isArray(transactionData.all_materials)) {
      return transactionData.all_materials.reduce((acc, item) => {
        return acc + (parseFloat(item.tax_amount) || 0);
      }, 0);
    }
    return 0;
  };

  const totalTax = calculateTotalTax();

  const refundPurpose =
    type === "refund"
      ? transactionData?.refund_purpose ||
        transactionData?.refundPurpose ||
        transactionData?.refund_reason ||
        ""
      : "";

  const remarksText =
    transactionData?.comments || transactionData?.remarks || "";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {organization.organization_name && (
              <Text style={styles.orgName}>
                {organization.organization_name}
              </Text>
            )}
            {organization.business_address && (
              <Text style={styles.orgDetails}>
                {organization.business_address}
              </Text>
            )}
            {(organization.city ||
              organization.state ||
              organization.pincode) && (
              <Text style={styles.orgDetails}>
                {[organization.city, organization.state, organization.pincode]
                  .filter(Boolean)
                  .join(", ")}
              </Text>
            )}
            {organization.user_email && (
              <Text style={styles.orgDetails}>
                Email: {organization.user_email}
              </Text>
            )}
            {organization.website && (
              <Text style={styles.orgDetails}>Web: {organization.website}</Text>
            )}
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.receiptTitle}>{getReceiptTitle()}</Text>
            <Text style={styles.receiptDetails}>
              Receipt No: {receiptNumber}
            </Text>
            <Text style={styles.receiptDetails}>
              Date:{" "}
              {formatDate(
                transactionData.date ||
                  transactionData.transaction_date ||
                  transactionData.created_on,
              )}
            </Text>
          </View>
        </View>

        {/* Transaction Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Transaction Details</Text>

          {/* Site Details inside Transaction Details */}
          {siteData.site_name && (
            <View
              style={{
                marginBottom: 8,
                paddingBottom: 8,
                borderBottomWidth: 1,
                borderBottomColor: "#EEEEEE",
              }}
            >
              <View style={styles.row}>
                <Text
                  style={[
                    styles.label,
                    { fontWeight: "bold", color: "#333333" },
                  ]}
                >
                  Site Name
                </Text>
                <Text style={styles.value}>{siteData.site_name}</Text>
              </View>
              {siteData.address && (
                <View style={styles.row}>
                  <Text style={styles.label}>Site Location</Text>
                  <Text style={styles.value}>{siteData.address}</Text>
                </View>
              )}
            </View>
          )}

          {type === "material" && (
            <>
              <View style={styles.row}>
                <Text style={styles.label}>Vendor/Shop</Text>
                <Text style={styles.value}>
                  {transactionData.vendor_name || "N/A"}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Contact</Text>
                <Text style={styles.value}>
                  {transactionData.contact_no || transactionData.vendor_contact}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Payment Method</Text>
                <Text style={styles.value}>
                  {transactionData.payment_method ||
                    transactionData.method ||
                    "N/A"}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Payment Status</Text>
                <Text style={styles.value}>
                  {transactionData.payment_status ||
                    transactionData.status ||
                    "N/A"}
                </Text>
              </View>
            </>
          )}

          {type === "worker" && (
            <>
              <View style={styles.row}>
                <Text style={styles.label}>Worker Name</Text>
                <Text style={styles.value}>
                  {transactionData.worker_name || "N/A"}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Work Type</Text>
                <Text style={styles.value}>
                  {transactionData.worker_workname || "N/A"}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Contact</Text>
                <Text style={styles.value}>
                  {transactionData?.worker_phone ||
                    transactionData.worker_contact ||
                    "N/A"}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Payment Method</Text>
                <Text style={styles.value}>
                  {transactionData.transaction_method ||
                    transactionData.method ||
                    "N/A"}
                </Text>
              </View>
            </>
          )}

          {/* ... other types (refund, other, vendor, contractor) can be added as needed, similarly structured ... */}
          {type === "refund" && (
            <>
              <View style={styles.row}>
                <Text style={styles.label}>Transaction Type</Text>
                <Text style={styles.value}>{"Refund"}</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Purpose of Refund</Text>
                <Text style={styles.value}>{refundPurpose || "N/A"}</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Refund Method</Text>
                <Text style={styles.value}>
                  {transactionData.transaction_method ||
                    transactionData.method ||
                    "N/A"}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Refund Status</Text>
                <Text style={styles.value}>
                  {transactionData.transaction_status ||
                    transactionData.status ||
                    "N/A"}
                </Text>
              </View>
            </>
          )}

          {type === "other" && (
            <>
              <View style={styles.row}>
                <Text style={styles.label}>Transaction Type</Text>
                <Text style={styles.value}>
                  {transactionData.transaction_type ||
                    transactionData.description ||
                    "N/A"}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Created By</Text>
                <Text style={styles.value}>
                  {transactionData.created_by || "N/A"}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Payment Method</Text>
                <Text style={styles.value}>
                  {transactionData.transaction_method ||
                    transactionData.method ||
                    "N/A"}
                </Text>
              </View>
            </>
          )}

          {type === "received" && (
            <>
              <View style={styles.row}>
                <Text style={styles.label}>Transaction Type</Text>
                <Text style={styles.value}>
                  {transactionData.transaction_type ||
                    transactionData.description ||
                    "N/A"}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Transaction Category</Text>
                <Text style={styles.value}>Received Payment</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Created By</Text>
                <Text style={styles.value}>
                  {transactionData.created_by || "N/A"}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Payment Method</Text>
                <Text style={styles.value}>
                  {transactionData.transaction_method ||
                    transactionData.method ||
                    "N/A"}
                </Text>
              </View>
            </>
          )}
        </View>

        {/* Materials List for Material Purchase */}
        {type === "material" &&
          transactionData.all_materials &&
          transactionData.all_materials.length > 0 && (
            <View style={{ marginBottom: 15 }}>
              <Text style={styles.sectionTitle}>Items / Materials</Text>
              <View style={styles.table}>
                {/* Table Header */}
                <View style={styles.tableRow}>
                  <View style={[styles.tableColHeader, { width: "26%" }]}>
                    <Text style={styles.tableCellHeader}>Material</Text>
                  </View>
                  <View style={[styles.tableColHeader, { width: "15%" }]}>
                    <Text
                      style={[styles.tableCellHeader, { textAlign: "center" }]}
                    >
                      Qty
                    </Text>
                  </View>
                  <View style={[styles.tableColHeader, { width: "15%" }]}>
                    <Text
                      style={[styles.tableCellHeader, { textAlign: "center" }]}
                    >
                      Price
                    </Text>
                  </View>
                  <View style={[styles.tableColHeader, { width: "15%" }]}>
                    <Text
                      style={[styles.tableCellHeader, { textAlign: "center" }]}
                    >
                      Tax
                    </Text>
                  </View>
                  <View style={[styles.tableColHeader, { width: "27%" }]}>
                    <Text
                      style={[styles.tableCellHeader, { textAlign: "center" }]}
                    >
                      Total
                    </Text>
                  </View>
                </View>
                {/* Table Body */}
                {transactionData.all_materials.map((material, index) => (
                  <View style={styles.tableRow} key={index}>
                    <View style={[styles.tableCol, { width: "26%" }]}>
                      <Text style={styles.tableCell}>
                        {material.material_name || material.name}
                      </Text>
                      <Text
                        style={[
                          styles.tableCell,
                          { fontSize: 7, color: "#999" },
                        ]}
                      >
                        {material.material_subcategory || material.category}
                      </Text>
                    </View>
                    <View style={[styles.tableCol, { width: "15%" }]}>
                      <Text style={[styles.tableCell, { textAlign: "center" }]}>
                        {material.quantity || material.qty}{" "}
                        {material.unit || material.unit_type}
                      </Text>
                    </View>
                    <View style={[styles.tableCol, { width: "15%" }]}>
                      <Text style={[styles.tableCell, { textAlign: "center" }]}>
                        {parseFloat(material.unit_price).toLocaleString(
                          "en-IN",
                        )}
                      </Text>
                    </View>
                    <View style={[styles.tableCol, { width: "15%" }]}>
                      <Text style={[styles.tableCell, { textAlign: "center" }]}>
                        {parseFloat(material.tax_amount || 0).toLocaleString(
                          "en-IN",
                        )}
                      </Text>
                    </View>
                    <View style={[styles.tableCol, { width: "27%" }]}>
                      <Text style={[styles.tableCell, { textAlign: "center" }]}>
                        {parseFloat(
                          material.total_price || material.price,
                        ).toLocaleString("en-IN")}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            </View>
          )}

        {/* Payment Summary */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Payment Summary</Text>

          {type === "material" && (
            <>
              <View style={styles.row}>
                <Text style={styles.label}>Sub Total (Excl. Tax)</Text>
                <Text style={styles.value}>
                  Rs.{" "}
                  {(
                    parseFloat(transactionData.total_price || 0) - totalTax
                  ).toLocaleString("en-IN")}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Total Tax</Text>
                <Text style={styles.value}>
                  Rs. {totalTax.toLocaleString("en-IN")}
                </Text>
              </View>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Total Amount</Text>
                <Text style={styles.totalValue}>
                  Rs.{" "}
                  {parseFloat(transactionData.total_price || 0).toLocaleString(
                    "en-IN",
                  )}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Paid Amount</Text>
                <Text style={styles.value}>
                  Rs.{" "}
                  {parseFloat(transactionData.paid_amount || 0).toLocaleString(
                    "en-IN",
                  )}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Remaining Amount</Text>
                <Text style={styles.value}>
                  Rs.{" "}
                  {parseFloat(
                    transactionData.remaining_payment || 0,
                  ).toLocaleString("en-IN")}
                </Text>
              </View>
            </>
          )}

          {(type === "worker" ||
            type === "refund" ||
            type === "other" ||
            type === "received") && (
            <>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>
                  {type === "received" ? "Total Received" : "Total Payment"}
                </Text>
                <Text style={styles.totalValue}>
                  Rs.{" "}
                  {parseFloat(transactionData.amount || 0).toLocaleString(
                    "en-IN",
                  )}
                </Text>
              </View>
              {type === "worker" &&
                transactionData.remaining_amount !== undefined && (
                  <View style={styles.row}>
                    <Text style={styles.label}>Remaining Amount</Text>
                    <Text style={styles.value}>
                      Rs.{" "}
                      {parseFloat(
                        transactionData.remaining_amount || 0,
                      ).toLocaleString("en-IN")}
                    </Text>
                  </View>
                )}
            </>
          )}
        </View>

        {/* Payment History for Material */}
        {type === "material" && paymentHistory && paymentHistory.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Payment History</Text>
            <View style={styles.table}>
              {/* Table Header */}
              <View style={styles.tableRow}>
                <View style={[styles.tableColHeader, { width: "25%" }]}>
                  <Text style={styles.tableCellHeader}>Date</Text>
                </View>
                <View style={[styles.tableColHeader, { width: "25%" }]}>
                  <Text style={styles.tableCellHeader}>Method</Text>
                </View>
                <View style={[styles.tableColHeader, { width: "25%" }]}>
                  <Text
                    style={[styles.tableCellHeader, { textAlign: "right" }]}
                  >
                    Amount
                  </Text>
                </View>
                <View style={[styles.tableColHeader, { width: "25%" }]}>
                  <Text style={styles.tableCellHeader}>Receipt</Text>
                </View>
              </View>
              {/* Table Body */}
              {paymentHistory.map((payment, index) => (
                <View key={index}>
                  <View style={styles.tableRow}>
                    <View style={[styles.tableCol, { width: "25%" }]}>
                      <Text style={styles.tableCell}>
                        {formatDate(payment.created_on || payment.date)}
                      </Text>
                    </View>
                    <View style={[styles.tableCol, { width: "25%" }]}>
                      <Text style={styles.tableCell}>
                        {payment.payment_mathod ||
                          payment.payment_method ||
                          "N/A"}
                      </Text>
                      {payment.remarks && (
                        <Text
                          style={[
                            styles.tableCell,
                            { fontSize: 7, color: "#999", fontStyle: "italic" },
                          ]}
                        >
                          {payment.remarks}
                        </Text>
                      )}
                    </View>
                    <View style={[styles.tableCol, { width: "25%" }]}>
                      <Text style={[styles.tableCell, { textAlign: "right" }]}>
                        {parseFloat(
                          payment.amount || payment.paid_amount || 0,
                        ).toLocaleString("en-IN")}
                      </Text>
                    </View>
                    <View style={[styles.tableCol, { width: "25%" }]}>
                      {payment.file ? (
                        <Text style={[styles.tableCell, { color: "#0066cc" }]}>
                          Attached below
                        </Text>
                      ) : (
                        <Text style={[styles.tableCell, { color: "#999" }]}>
                          -
                        </Text>
                      )}
                    </View>
                  </View>
                  {/* Append payment receipt image if available */}
                  {payment.file && (
                    <View
                      style={{
                        padding: 5,
                        alignItems: "center",
                        borderBottomWidth: 1,
                        borderBottomColor: "#EEEEEE",
                      }}
                    >
                      <Image
                        src={payment.file}
                        style={{
                          width: 150,
                          height: 150,
                          objectFit: "contain",
                        }}
                      />
                      <Text
                        style={{ fontSize: 7, color: "#666", marginTop: 2 }}
                      >
                        Receipt for payment on{" "}
                        {formatDate(payment.created_on || payment.date)}
                      </Text>
                    </View>
                  )}
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Remarks */}
        {remarksText &&
          !(
            type === "refund" &&
            refundPurpose &&
            remarksText === refundPurpose
          ) && (
            <View style={styles.remark}>
              <Text style={styles.sectionTitle}>Remarks</Text>
              <Text style={styles.remarkText}>{remarksText}</Text>
            </View>
          )}

        {/* Receipt Image */}
        {/* {((transactionData.receipt_image && transactionData.receipt_image.length !== 0) || transactionData.receipt_url || transactionData.ireceipt_image) && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Receipt Image</Text>
            {(() => {
              let images = [];
              if (transactionData.receipt_image) {
                images = Array.isArray(transactionData.receipt_image)
                  ? transactionData.receipt_image
                  : [transactionData.receipt_image];
              } else if (transactionData.receipt_url) {
                images = [transactionData.receipt_url];
              }
              if (transactionData.ireceipt_image) {
                images.push(transactionData.ireceipt_image);
              }

              const uniqueImages = [...new Set(images)]; // Remove duplicates

              return uniqueImages.map((imgSrc, idx) => (
                <Image key={idx} src={imgSrc} style={styles.receiptImage} />
              ));
            })()}
          </View>
        )} */}

        {/* Receipt Image */}

        {/* Footer */}
        <View style={styles.footer}>
          <Text>
            This is a computer-generated receipt and does not require signature.
          </Text>
          <Text>Generated by Site Master</Text>
        </View>
      </Page>
      {/* Second page: full-size receipt image(s) */}
      {(() => {
        let images = [];
        if (transactionData.receipt_image) {
          images = Array.isArray(transactionData.receipt_image)
            ? transactionData.receipt_image
            : [transactionData.receipt_image];
        } else if (transactionData.receipt_url) {
          images = [transactionData.receipt_url];
        }
        if (transactionData.ireceipt_image) {
          images.push(transactionData.ireceipt_image);
        }
        const uniqueImages = [...new Set(images)];

        return uniqueImages.map((imgSrc, idx) => (
          <Page key={idx} size="A4" style={{ padding: 0, margin: 0 }}>
            <Image
              src={imgSrc}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </Page>
        ));
      })()}
    </Document>
  );
};

export default ReceiptPDF;
